import React from "react"
import { BodyText, InlineText, SubTitle } from "../../Typography/Typography"
import SmartLink from "../../layout/SmartLink/SmartLink"

const providerProfileClickEvent = () => ({
  name: "SEO Filtered Directory - Copy link to provider clicked",
  properties: {
    "Care Type": "care_home",
    "GEO Location": "Northampton",
    "Current URL":
      "https://www.caresourcer.com/s/providers/towns/northampton/care-home/",
  },
})

export default {
  summary: (
    <BodyText marginBottom>
      Choosing a care home for a loved one can be difficult, however with 58
      care homes in Northampton, and 41 rated Good or Outstanding by the Care
      Quality Commision (CQC), there’s plenty to choose from. Here are a few of
      our top tips to help you narrow your options and find the right care home.
    </BodyText>
  ),
  content: [
    <SubTitle marginBottom bold>
      Location is key
    </SubTitle>,
    <BodyText marginBottom>
      When researching care homes, think about where visitors will be travelling
      from and what transportation they have access to. In Northampton, the
      public transport is excellent and you will find most care homes are on
      well serviced bus routes, or have small car parks. It is always best when
      you speak to a home to ask about transport options, as you may find it is
      just as easy for someone to visit something centrally located as it is to
      go slightly out of town, to a care home like the{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-145040652/brampton-view-care-home"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Brampton View Care Home</InlineText>
      </SmartLink>{" "}
      which is in the picturesque Northamptonshire countryside and yet only
      10-minutes drive from Northampton town center.
    </BodyText>,
    <SubTitle marginBottom bold>
      Feeling at home
    </SubTitle>,
    <BodyText marginBottom>
      As well as thinking about how friends and family will visit, or which part
      of Northampton your loved one currently lives in, it is vital to think
      about their lifestyle and what would help make them feel happy doing in
      the care home. Below are a few things to consider that will help make this
      change as smooth as possible for your loved one:
    </BodyText>,
    <SubTitle marginBottom bold italic>
      Gardens and outdoor spaces
    </SubTitle>,
    <BodyText marginBottom>
      Having outdoor space can mean a lot to people, but think specifically
      about what your loved one likes to do. If they are a keen gardener or have
      been growing vegetables for years and want to continue when at the care
      home, then we would recommend checking with the individual homes to
      understand what is possible. However, if they just want somewhere that
      they can sit and enjoy the sun, then perhaps somewhere with a slightly
      smaller garden but with a lovely seating area like{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-125855782/collingtree-park"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Collingtree Park</InlineText>
      </SmartLink>{" "}
      would suit better.
    </BodyText>,
    <BodyText marginBottom bold italic>
      Pets welcome
    </BodyText>,
    <BodyText marginBottom>
      Many care homes in Northampton are pet friendly, with most having
      regularly organised ‘therapet’ visits, where calm and friendly dogs and
      cats visit to provide companionship to animal-loving residents. There are
      a number of care homes, like{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-2460962079/burlington-court"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Burlington Court</InlineText>
      </SmartLink>
      , which would allow your loved one’s pet to visit them frequently when in
      the home and some may even allow the pet to move in with the resident. It
      is recommended to speak to the care home to find out more about their pet
      policy, if this is something that is important to your loved one.
    </BodyText>,
    <BodyText marginBottom bold italic>
      Activities and days out
    </BodyText>,
    <BodyText marginBottom>
      All care homes in Northampton offer residents a wide range of activities,
      including exercise classes, movie nights, cooking or arts and crafts.
      However, they all also have quiet rooms and spaces if people would like to
      read or spend some time reminiscing. Many, like the{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-2522275591/ashurst-mews-care-home"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Ashurst Mews Care Home</InlineText>
      </SmartLink>
      , also try to organise days out to the Royal & Derngate Theatre, Althorp
      House or simply to go get some fish and chips. They also plan a variety of
      entertainment in the home, for example monthly visits of exotic animals
      and birds from the team at Zoo Lab. Care homes are always looking for new
      ideas and to help people feel more at home, so be sure to ask what
      activities homes offer residents when you call.
    </BodyText>,
  ],
}
